import { Badge } from '@react-apps/design-system';
import type { ResolvedPerk } from '@/modules/catalog/presentation/perk-index';
import type { EffectivePerk } from '../../domain/entities/user.entity';

interface DerivedPerksBlockProps {
  derived: EffectivePerk[];
  perkIndex: Map<string, ResolvedPerk>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * "Também tem por composição": o que o perfil tem **sem** pacote próprio, vindo
 * de `grantsPerkKeys` de algo concedido. Read-only de propósito — derivado não
 * tem `grantedPerkId`, não se revoga, e qualquer controle aqui prometeria uma
 * ação que o back não tem como executar.
 */
export function DerivedPerksBlock({
  derived,
  perkIndex,
  isLoading,
  isError,
}: DerivedPerksBlockProps) {
  if (isError) {
    return (
      <p role="status" className="text-sm text-amber-700 dark:text-amber-400">
        Não foi possível carregar o que este perfil tem por composição — os
        avisos de concessão redundante e de revogação inócua estão desligados.
      </p>
    );
  }
  // Enquanto carrega, os avisos ainda não sabem o que vem por composição: dizer
  // isso evita o operador montar o lote achando que foi checado.
  if (isLoading) {
    return (
      <p role="status" className="text-(--color-text-secondary) text-sm">
        Verificando o que este perfil tem por composição…
      </p>
    );
  }
  if (derived.length === 0) return null;

  return (
    <section className="space-y-2 pt-2">
      <h4 className="text-(--color-text-primary) text-sm font-semibold">
        Também tem por composição
      </h4>
      <p className="text-(--color-text-secondary) text-xs">
        Vem incluído em outro acesso deste perfil. Não há pacote a revogar: sai
        quando sair o acesso que o inclui.
      </p>
      <ul className="space-y-2">
        {derived.map((perk) => {
          const name = perkIndex.get(perk.perkId)?.group.name;
          return (
            <li
              key={perk.perkId}
              className="border-(--color-border) rounded-md border border-dashed px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-(--color-text-primary) text-sm">
                  {name ?? perk.perkKey}
                </span>
                {/*
                  O selo viaja com o item, não com o bloco: no dia em que uma
                  busca listar acessos fora do agrupamento, a linha ainda se
                  explica sozinha.
                */}
                <Badge variant="info">Por composição</Badge>
              </div>
              <span className="text-(--color-text-secondary) block text-xs">
                {name && <span className="font-mono">{perk.perkKey} · </span>}
                vem de{' '}
                <span className="font-mono">{parentKeys(perk).join(', ')}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A mesma key pode ter dois perks-pai (um por política): nomeia uma vez. */
function parentKeys(perk: EffectivePerk): string[] {
  return [...new Set(perk.derivedFrom.map((parent) => parent.perkKey))];
}
